import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { mockCryptoAssets, mockCryptoHoldings } from "../../mocks/crypto";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import useAuthStatus from "../../hooks/useAuthStatus";
import { getUserWallet, withdrawCryptoAPI } from "../../api/apiServices";
import { getHoldingColor, getHoldingIcon } from "../home/page";


export default function WithdrawCrypto() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStatus();
  const [step, setStep] = useState("form");
  const [selectedSymbol, setSelectedSymbol] = useState("USDT");
  const [address, setAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [liveRates, setLiveRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [nairaBalance, setNairaBalance] = useState(0);

  const handleConfirm = async () => {
  setStep("processing");
  setError(null); // Clear previous errors

  try {
    const payload = {
      currency: selectedSymbol,
      network: holding?.rawNetwork,
      toAddress: address,
      amount: String(amount),
    };

    const response = await withdrawCryptoAPI(payload);

    if (response.status === true) {
      setStep("success");
    } else {
      // Handle cases where status is false but no exception was thrown
      setError(response.message || "Withdrawal failed. Please try again.");
      setStep("form");
    }
  } catch (err) {
    console.error("Withdrawal Error:", err);
    // Extract error message from API response if available
    const errorMsg = err.response?.data?.message || "An unexpected error occurred.";
    setError(errorMsg);
    setStep("form"); // Send user back to form to fix errors
    
    // Optional: Use a toast notification here if you have one
    alert(errorMsg); 
  }
};

  useEffect(() => {
  if (!isAuthenticated) return;

  const fetchAllData = async () => {
    try {
      // 1. Fetch User Wallet
      const walletRes = await getUserWallet();
      if (walletRes.status === true) {
        setWalletData(walletRes.data);
        setNairaBalance(walletRes.data.nairaBalance || 0);
      }

      // 2. Fetch Live Rates from CoinGecko
      const ratesRes = await fetch(
        "https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,tether,binancecoin,solana,ripple,dogecoin,cardano&vs_currencies=ngn"
      );
      const ratesData = await ratesRes.json();

      const rateMap = {
        BTC: ratesData.bitcoin?.ngn || 0,
        ETH: ratesData.ethereum?.ngn || 0,
        USDT: ratesData.tether?.ngn || 1558,
        BNB: ratesData.binancecoin?.ngn || 0,
        SOL: ratesData.solana?.ngn || 0,
        XRP: ratesData.ripple?.ngn || 0,
        DOGE: ratesData.dogecoin?.ngn || 0,
        ADA: ratesData.cardano?.ngn || 0,
        NOT: ratesData.notcoin?.ngn || 0,
      };
      setLiveRates(rateMap);

    } catch (err) {
      console.error("Failed to load portfolio:", err);
    setError(err);
  } finally {
    setLoading(false);
  }
  };

  fetchAllData();
}, [isAuthenticated]);


const realHoldings = useMemo(() => {
  if (!walletData?.wallets || !liveRates) return [];

  // Helper to make network names look pretty
  const formatNetwork = (net) => {
    const maps = {
      bsc: "BNB Smart Chain (BEP20)",
      eth: "Ethereum (ERC20)",
      ton: "TON Network",
      solana: "Solana",
      tron: "TRC20"
    };
    return maps[net] || net.toUpperCase();
  };

  return walletData.wallets
    .filter(wallet => (wallet.balance || 0) > 0)
    .map(wallet => {
      const rate = liveRates[wallet.currency] || 0;
      const valueNGN = (wallet.balance || 0) * rate;

      return {
        symbol: wallet.currency,
        amount: wallet.balance || 0,
        valueNGN: valueNGN,
        network: formatNetwork(wallet.network), // Add this line
        rawNetwork: wallet.network,
        color: getHoldingColor(wallet.currency),
        icon: getHoldingIcon(wallet.currency),
      };
    })
    .sort((a, b) => b.valueNGN - a.valueNGN);
}, [walletData, liveRates]);

const fee = 0.0001;
  const rate = liveRates[selectedSymbol] || 0;
  const nairaEquiv = amount
  ? (parseFloat(amount) * rate).toFixed(2)
  : "0";


const holding = useMemo(() => {
  return realHoldings.find(h => h.symbol === selectedSymbol) || realHoldings[0];
}, [realHoldings, selectedSymbol]);

// Use the network from the holding object directly
const currentNetwork = holding?.network || "Select Asset";


  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-28">
      <div className="bg-white px-5 pt-12 pb-4 flex items-center gap-3 border-b border-slate-100">
        <button onClick={() => step === "form" ? navigate("/home") : setStep("form")} className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center cursor-pointer hover:bg-slate-200">
          <i className="ri-arrow-left-line text-slate-600 text-lg"></i>
        </button>
        <h1 className="text-slate-800 font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Withdraw Crypto</h1>
      </div>

      <div className="px-5 py-4">
  {/* ================= FORM ================= */}
  {step === "form" && (
    <div>

      {error && (
      <div className="bg-red-50 border border-red-100 text-red-600 p-3 rounded-xl mb-4 text-xs flex items-center gap-2">
        <i className="ri-error-warning-line"></i>
        {typeof error === 'string' ? error : "Transaction failed. Please check your details."}
      </div>
    )}
      <p className="text-slate-500 text-sm mb-4">
        Send crypto to any external wallet
      </p>

      {/* ===== HOLDINGS SELECTOR ===== */}
      {loading ? (
        <div className="text-sm text-slate-400 py-6">
          Loading portfolio...
        </div>
      ) : !realHoldings.length ? (
        <div className="text-sm text-slate-400 py-6">
          No crypto holdings available
        </div>
      ) : (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-5">
          {realHoldings.map((h) => (
            <button
              key={h.symbol}
              onClick={() => setSelectedSymbol(h.symbol)}
              className="flex-shrink-0 flex flex-col items-center gap-1 px-4 py-2 rounded-xl border transition-all cursor-pointer"
              style={{
                background:
                  selectedSymbol === h.symbol
                    ? `${h.color}15`
                    : "#fff",
                borderColor:
                  selectedSymbol === h.symbol
                    ? h.color
                    : "#E2E8F0",
              }}
            >
              <span
                className="text-xs font-bold"
                style={{
                  color:
                    selectedSymbol === h.symbol
                      ? h.color
                      : "#94A3B8",
                }}
              >
                {h.symbol}
              </span>

              <span className="text-slate-400 text-xs">
                {Number(h.amount).toLocaleString(undefined, {
                  maximumFractionDigits: 6,
                })}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* ===== FORM INPUTS ===== */}
      <div className="space-y-4">
        {/* ADDRESS */}
        <div>
          <label className="text-slate-600 text-xs font-medium mb-2 block">
            Recipient Wallet Address
          </label>

          <div className="relative">
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value.trim())}
              placeholder={`Enter ${selectedSymbol} wallet address`}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl py-4 pl-4 pr-12 text-slate-800 text-sm placeholder-slate-300 focus:outline-none focus:border-emerald-500 focus:bg-white transition-colors"
            />

            <button className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center cursor-pointer">
              <i className="ri-qr-scan-line text-slate-400 text-lg"></i>
            </button>
          </div>
        </div>

        {/* AMOUNT */}
        <div>
          <label className="text-slate-600 text-xs font-medium mb-2 block">
            Amount ({selectedSymbol})
          </label>

          <div className="relative">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00000000"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl py-4 pl-4 pr-16 text-slate-800 text-sm placeholder-slate-300 focus:outline-none focus:border-emerald-500 focus:bg-white transition-colors"
            />

            <button
              onClick={() =>
                setAmount(String(holding?.amount || 0))
              }
              className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-600 text-xs cursor-pointer whitespace-nowrap font-semibold bg-emerald-50 px-2 py-1 rounded-lg"
            >
              MAX
            </button>
          </div>

          {/* INFO */}
          <p className="text-slate-400 text-xs mt-1">
            Available:{" "}
            {Number(holding?.amount || 0).toLocaleString()}{" "}
            {selectedSymbol} · ≈ ₦
            {Number(nairaEquiv || 0).toLocaleString()}
          </p>

          {/* VALIDATION */}
          {parseFloat(amount || 0) >
            parseFloat(holding?.amount || 0) && (
            <p className="text-red-500 text-xs mt-1">
              Insufficient balance
            </p>
          )}
        </div>
      </div>

      {/* ===== FEES ===== */}
      <div className="bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 space-y-2 my-5">
        <div className="flex justify-between text-xs">
          <span className="text-slate-400">Network Fee</span>
          <span className="text-slate-600">
            {fee} {selectedSymbol}
          </span>
        </div>

        <div className="flex justify-between text-xs">
          <span className="text-slate-400">Network</span>
          <span className="text-slate-600">
            {currentNetwork}
          </span>
        </div>
      </div>

      {/* ===== WARNING ===== */}
      <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-6 flex items-start gap-3">
        <i className="ri-alert-line text-red-500 text-lg mt-0.5"></i>
        <p className="text-red-600 text-xs leading-relaxed">
          Double-check the wallet address. Crypto withdrawals are{" "}
          <strong>irreversible</strong>.
        </p>
      </div>

      {/* ===== CTA ===== */}
      <button
        onClick={() => setStep("confirm")}
        disabled={
          !address ||
          !amount ||
          parseFloat(amount) <= 0 ||
          parseFloat(amount) >
            parseFloat(holding?.amount || 0)
        }
        className="w-full py-4 rounded-2xl text-white font-semibold text-base transition-all active:scale-95 disabled:opacity-40 bg-emerald-600 hover:bg-emerald-700"
      >
        Continue
      </button>
    </div>
  )}

  {/* ================= CONFIRM ================= */}
  {step === "confirm" && (
    <div>
      <h2 className="text-slate-800 text-xl font-bold mb-1">
        Confirm Withdrawal
      </h2>

      <p className="text-slate-400 text-sm mb-6">
        This action is irreversible
      </p>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4 mb-6">
        <div className="flex justify-between">
          <span className="text-slate-400 text-sm">Asset</span>
          <span className="text-slate-800 font-bold">
            {selectedSymbol}
          </span>
        </div>

        <div className="flex justify-between">
          <span className="text-slate-400 text-sm">Amount</span>
          <span className="text-slate-800 font-bold">
            {amount} {selectedSymbol}
          </span>
        </div>

        <div className="flex justify-between">
          <span className="text-slate-400 text-sm">
            Network Fee
          </span>
          <span className="text-slate-600">
            {fee} {selectedSymbol}
          </span>
        </div>

        <div className="flex justify-between">
          <span className="text-slate-400 text-sm">
            Network
          </span>
          <span className="text-slate-600">
            {currentNetwork}
          </span>
        </div>

        <div className="border-t pt-3">
          <span className="text-slate-400 text-xs block mb-1">
            To Address
          </span>
          <span className="text-slate-700 text-xs break-all font-mono">
            {address}
          </span>
        </div>
      </div>

      <button
        onClick={handleConfirm}
        className="w-full py-4 rounded-2xl text-white font-semibold bg-emerald-600 hover:bg-emerald-700"
      >
        Confirm Withdrawal
      </button>
    </div>
  )}

  {/* ================= PROCESSING ================= */}
  {step === "processing" && (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
      <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
        <i className="ri-loader-4-line text-emerald-600 text-4xl animate-spin"></i>
      </div>

      <h2 className="text-slate-800 text-xl font-bold mb-2">
        Broadcasting Transaction
      </h2>

      <p className="text-slate-400 text-sm">
        Sending {amount} {selectedSymbol}...
      </p>
    </div>
  )}

  {/* ================= SUCCESS ================= */}
  {step === "success" && (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
      <div className="w-20 h-20 rounded-full bg-emerald-100 border-2 border-emerald-200 flex items-center justify-center mb-6">
        <i className="ri-check-line text-emerald-600 text-4xl"></i>
      </div>

      <h2 className="text-slate-800 text-2xl font-bold mb-2">
        Withdrawal Sent!
      </h2>

      <p className="text-slate-400 text-sm mb-6">
        Broadcasted to {currentNetwork}
      </p>

      <div className="bg-emerald-50 border border-emerald-100 rounded-2xl px-6 py-4 mb-8 w-full max-w-xs">
        <p className="text-slate-400 text-xs mb-1">
          Amount Sent
        </p>

        <p className="text-emerald-600 text-3xl font-bold">
          {amount} {selectedSymbol}
        </p>
      </div>

      <button
        onClick={() => navigate("/home")}
        className="w-full max-w-xs py-4 rounded-2xl text-white font-semibold bg-emerald-600 hover:bg-emerald-700"
      >
        Back to Home
      </button>
    </div>
  )}
</div>
      <MobileBottomNav />
    </div>
  );
}
